import { useState } from 'react';
import type { ReactNode } from 'react';

import MenuIcon from '@mui/icons-material/Menu';
import { Collapse, Drawer, IconButton, List } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import CloseIcon from '@mui/icons-material/Close';

import { translate } from '@openclassrooms/translations';
import { RouteChangeListener, ROUTES } from '@openclassrooms/routing';
import { getLocalisationConfig } from '@openclassrooms/localisation/services';

import { styled } from '@classify/react-core-components/styles';
import Divider from '@classify/react-core-components/Divider';
import Typography from '@classify/react-core-components/Typography';
import Box from '@classify/react-core-components/Box';

import mainNavigation from '../../services/mainNavigation';
import MainHeaderLogo from '../MainHeaderLogo';
import StyledListItem from './StyledListItem';
import StyledListItemText from './StyledListItemText';
import LinkListItem from './LinkListItem';
import OverTitleListItem from './StyledOverTitle';

interface Props {
  isLegacy: boolean;
  hasRouter: boolean;
  additionalMenu?: ReactNode;
}

const StyledDivider = styled(Divider)(({ theme: { palette, spacing } }) => ({
  '&&': {
    backgroundColor: palette.background.alternative,
  },
  marginLeft: spacing(2),
  marginRight: spacing(2),
}));

const MainHeaderDrawer = ({ isLegacy, hasRouter, additionalMenu }: Props) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isTrainingMenuOpen, setIsTrainingMenuOpen] = useState(false);
  const [isApprenticeshipMenuOpen, setIsApprenticeshipMenuOpen] = useState(false);
  const [isBusinessMenuOpen, setIsBusinessMenuOpen] = useState(false);
  const navigationConfig = mainNavigation();

  const canHideDrawerOnRouteChange = !isLegacy || hasRouter;
  const handleToggleTrainingMenu = () => setIsTrainingMenuOpen((prev) => !prev);
  const handleToggleApprenticeshipMenu = () => setIsApprenticeshipMenuOpen((prev) => !prev);
  const handleToggleBusinessMenu = () => setIsBusinessMenuOpen((prev) => !prev);

  return (
    <div>
      {canHideDrawerOnRouteChange && (
        <RouteChangeListener
          onChange={(location, hash) => {
            if (location.pathname === ROUTES.DASHBOARD.COURSES && hash === '') {
              return;
            }
            setIsDrawerOpen(false);
          }}
          shouldFireOnAnchorChange
        />
      )}
      <Drawer
        sx={{
          '& .MuiDrawer-paper': {
            width: { sx: 264, sm: 350 },
          },
        }}
        open={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
        }}
      >
        <Box sx={{ display: 'flex', direction: 'row', alignItems: 'center' }}>
          <IconButton
            aria-label="close"
            onClick={() => {
              setIsDrawerOpen(false);
            }}
            size="large"
            sx={{ display: 'block', color: 'palette.text.onMain', marginLeft: 0.5 }}
          >
            <CloseIcon sx={{ display: 'block' }} aria-label="close" />
          </IconButton>
          <MainHeaderLogo
            isLegacy={isLegacy}
            sx={{
              '& svg': {
                width: 117,
                marginLeft: 1,
              },
            }}
          />
        </Box>
        <Divider />

        {additionalMenu && (
          <>
            <Box sx={{ my: 2 }}>{additionalMenu}</Box>
            <Divider />
          </>
        )}

        <List component="nav">
          <StyledListItem
            button
            onClick={handleToggleTrainingMenu}
            sx={{ mb: isTrainingMenuOpen ? 0 : null }}
          >
            <StyledListItemText
              primary={
                <Typography variant={isTrainingMenuOpen ? 'subheading' : 'body'} component="span">
                  {navigationConfig.TRAINING.content}
                </Typography>
              }
            />
            {isTrainingMenuOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          </StyledListItem>

          <Collapse
            in={isTrainingMenuOpen}
            timeout="auto"
            unmountOnExit
            aria-label={translate('global.word.reduce')}
          >
            <List component="div" disablePadding sx={{ pl: 2 }}>
              <LinkListItem isLegacy={isLegacy} link={navigationConfig.OFFERS} isSubMenuItem />
              <OverTitleListItem>
                {translate('main_header.training_programs.dropdown.paths.overline')}
              </OverTitleListItem>
              <LinkListItem isLegacy={isLegacy} link={navigationConfig.PATHS} isSubMenuItem />
              <OverTitleListItem>
                {translate('main_header.training_programs.dropdown.courses.overline')}
              </OverTitleListItem>
              <LinkListItem isLegacy={isLegacy} link={navigationConfig.COURSES} isSubMenuItem />
            </List>
          </Collapse>

          {navigationConfig.APPRENTICESHIP.shouldBeDisplayed && (
            <>
              <StyledListItem
                button
                onClick={handleToggleApprenticeshipMenu}
                sx={{ mb: isApprenticeshipMenuOpen ? 0 : null }}
              >
                <StyledListItemText
                  primary={
                    <Typography
                      variant={isApprenticeshipMenuOpen ? 'subheading' : 'body'}
                      component="span"
                    >
                      {navigationConfig.APPRENTICESHIP.content}
                    </Typography>
                  }
                />
                {isApprenticeshipMenuOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
              </StyledListItem>

              <Collapse
                in={isApprenticeshipMenuOpen}
                timeout="auto"
                unmountOnExit
                aria-label={translate('global.word.reduce')}
              >
                <List component="div" disablePadding sx={{ pl: 2 }}>
                  <OverTitleListItem>
                    {translate('main_header.apprenticeship_dropdown.b2c.overline')}
                  </OverTitleListItem>
                  <LinkListItem
                    isLegacy={isLegacy}
                    link={navigationConfig.BECOME_AN_APPRENTICE}
                    isSubMenuItem
                  />
                  <LinkListItem
                    isLegacy={isLegacy}
                    link={navigationConfig.CONSULT_JOB_OFFERS}
                    isSubMenuItem
                  />
                  <OverTitleListItem>
                    {translate('main_header.apprenticeship_dropdown.business.overline')}
                  </OverTitleListItem>
                  <LinkListItem
                    isLegacy={isLegacy}
                    link={navigationConfig.RECRUIT_APPRENTICES}
                    isSubMenuItem
                  />
                  {getLocalisationConfig('apprenticeship.hasTalentPool') && (
                    <LinkListItem
                      isLegacy={isLegacy}
                      link={navigationConfig.FINALIZE_APPRENTICESHIP}
                      isSubMenuItem
                    />
                  )}
                </List>
              </Collapse>
            </>
          )}

          {navigationConfig.FUNDING.shouldBeDisplayed && (
            <LinkListItem isLegacy={isLegacy} link={navigationConfig.FUNDING} />
          )}

          <StyledDivider />
          <StyledListItem
            button
            onClick={handleToggleBusinessMenu}
            sx={{ mb: isBusinessMenuOpen ? 0 : null }}
          >
            <StyledListItemText
              primary={
                <Typography variant={isBusinessMenuOpen ? 'subheading' : 'body'} component="span">
                  {navigationConfig.BUSINESS.content}
                </Typography>
              }
            />
            {isBusinessMenuOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          </StyledListItem>

          <Collapse
            in={isBusinessMenuOpen}
            timeout="auto"
            unmountOnExit
            aria-label={translate('global.word.reduce')}
          >
            <List component="div" disablePadding sx={{ pl: 2 }}>
              <LinkListItem
                isLegacy={isLegacy}
                link={navigationConfig.BUSINESS_DISCOVER}
                isSubMenuItem
              />
              {getLocalisationConfig('page.uplift.url') && (
                <>
                  <OverTitleListItem>
                    {translate('main_header.business_dropdown.recruitment.overline')}
                  </OverTitleListItem>
                  <LinkListItem isLegacy={isLegacy} link={navigationConfig.UPLIFT} isSubMenuItem />
                  {getLocalisationConfig('apprenticeship.hasTalentPool') && (
                    <LinkListItem
                      isLegacy={isLegacy}
                      link={navigationConfig.FINALIZE_APPRENTICESHIP}
                      isSubMenuItem
                    />
                  )}
                </>
              )}
              <OverTitleListItem>
                {translate('main_header.business_dropdown.train.overline')}
              </OverTitleListItem>
              <LinkListItem isLegacy={isLegacy} link={navigationConfig.SPARK} isSubMenuItem />
              <LinkListItem isLegacy={isLegacy} link={navigationConfig.BOOST} isSubMenuItem />
              <LinkListItem isLegacy={isLegacy} link={navigationConfig.PIVOT} isSubMenuItem />
            </List>
          </Collapse>
        </List>
      </Drawer>

      <IconButton
        aria-label="menu"
        onClick={() => {
          setIsDrawerOpen(true);
        }}
        size="large"
        sx={{ display: 'block', marginRight: 1, color: 'palette.text.onMain', marginLeft: -1.5 }}
      >
        <MenuIcon aria-label={translate('global.word.menu')} sx={{ display: 'block' }} />
      </IconButton>
    </div>
  );
};

export default MainHeaderDrawer;
